import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { logout, setShowLoginModal, setShowRegisterModal, setShowFavoritesModal, getFavorites, getUserInfo } from '../store/authSlice';
import FavoritesModal from './FavoritesModal';
import SkillsModal from './SkillsModal';

const UserContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LoginButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.5);
    color: #ffffff;
  }
`;

const UserAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.6);
    transform: scale(1.05);
  }
`;

const UserName = styled.span`
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    color: rgba(255, 255, 255, 0.8);
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: rgba(30, 30, 30, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 8px 0;
  min-width: 180px;
  z-index: 1000;
  backdrop-filter: blur(20px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
`;

const MenuItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  font-size: 14px;
  color: #ffffff;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: rgba(102, 126, 234, 0.3);
  }
  
  &.danger {
    color: #ef4444;
    
    &:hover {
      background: rgba(239, 68, 68, 0.2);
    }
  }
`;

const MenuItemIcon = styled.span`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WeChatIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.504a.59.59 0 0 0 .634.736l1.939-.38a.59.59 0 0 1 .436.068 8.1 8.1 0 0 0 2.857.523c.276 0 .548-.014.815-.041-.853-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.353-8.6-6.353zM5.785 5.991c.642 0 1.162.52 1.162 1.162s-.52 1.162-1.162 1.162-1.162-.52-1.162-1.162.52-1.162 1.162-1.162zm5.813 0c.642 0 1.162.52 1.162 1.162s-.52 1.162-1.162 1.162-1.162-.52-1.162-1.162.52-1.162 1.162-1.162z"/>
  </svg>
);

const FavoriteIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
  </svg>
);

const LogoutIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
  </svg>
);

const SkillsIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
  </svg>
);

const UserInfo = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSkillsModal, setShowSkillsModal] = useState(false);
  const dispatch = useDispatch();
  const { user, isAuthenticated, showFavoritesModal } = useSelector(state => state.auth);
  const dropdownRef = useRef(null);

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 组件挂载时检查认证状态
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token && !isAuthenticated) {
        try {
          // 尝试获取用户信息来验证token
          await dispatch(getUserInfo()).unwrap();
          // 如果成功，获取收藏列表
          await dispatch(getFavorites()).unwrap();
        } catch (error) {
          // 如果token无效，清除它
          localStorage.removeItem('token');
          console.log('Token验证失败，已清除');
        }
      }
    };

    checkAuth();
  }, [dispatch, isAuthenticated]);

  const handleLogin = () => {
    dispatch(setShowLoginModal(true));
  };

  const handleRegister = () => {
    dispatch(setShowRegisterModal(true));
  };

  const handleLogout = () => {
    dispatch(logout());
    setShowDropdown(false);
  };

  const handleFavorites = () => {
    dispatch(setShowFavoritesModal(true));
    setShowDropdown(false);
  };

  const handleSkills = () => {
    setShowSkillsModal(true);
    setShowDropdown(false);
  };

  if (!isAuthenticated || !user) {
    return (
      <>
        <UserContainer>
          {/* <LoginButton onClick={handleLogin}>
            <WeChatIcon />
            登录
          </LoginButton> */}
          <LoginButton onClick={handleRegister}>
            注册
          </LoginButton>
        </UserContainer>
      </>
    );
  }

  return (
    <>
      <UserContainer ref={dropdownRef}>
        <UserAvatar 
          src={user.avatar || '/default-avatar.svg'} 
          alt={user.nickname}
          onClick={() => setShowDropdown(!showDropdown)}
        />
        <UserName onClick={() => setShowDropdown(!showDropdown)}>
          {user.nickname}
        </UserName>
        
        {showDropdown && (
          <DropdownMenu>
            <MenuItem onClick={handleSkills}>
              <MenuItemIcon>
                <SkillsIcon />
              </MenuItemIcon>
              Skills 配置
            </MenuItem>
            <MenuItem onClick={handleFavorites}>
              <MenuItemIcon>
                <FavoriteIcon />
              </MenuItemIcon>
              我的收藏
            </MenuItem>
            <MenuItem onClick={handleLogout} className="danger">
              <MenuItemIcon>
                <LogoutIcon />
              </MenuItemIcon>
              退出登录
            </MenuItem>
          </DropdownMenu>
        )}
      </UserContainer>
      
      <FavoritesModal
        isOpen={showFavoritesModal}
        onClose={() => dispatch(setShowFavoritesModal(false))}
      />
      <SkillsModal
        isOpen={showSkillsModal}
        onClose={() => setShowSkillsModal(false)}
      />
    </>
  );
};

export default UserInfo;